import { captureException } from "@sentry/nextjs";
import { unstable_isUnrecognizedActionError } from "next/navigation";
import { fromPromise } from "xstate";

import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import type {
  QuestionnaireByQuery,
  GeneralVisitReasonType,
} from "@/lib/__generated/enterpriseGraph.sdk";
import { getQuestionnaireBy } from "@/lib/services/enterprise/getQuestionnaireBy";
import { Language } from "@/types/scheduler-machine";

const QUESTIONNAIRE_VISIT_REASON: Record<DigitalVisitReasonEnum, GeneralVisitReasonType> = {
  [DigitalVisitReasonEnum.Emergency]: "EMERGENCY",
  [DigitalVisitReasonEnum.BrokenToothToothPain]: "BROKEN_TOOTH_TOOTH_PAIN",
  [DigitalVisitReasonEnum.Implants]: "IMPLANTS",
  [DigitalVisitReasonEnum.Dentures]: "DENTURES",
  [DigitalVisitReasonEnum.ClearAligners]: "CLEAR_ALIGNERS",
  [DigitalVisitReasonEnum.Other]: "OTHER",
  [DigitalVisitReasonEnum.HealthyMouthMovement]: "HEALTHY_MOUTH_MOVEMENT",
};

export const fetchQuestionsActor = fromPromise<
  QuestionnaireByQuery | null,
  { reasonForVisit: DigitalVisitReasonEnum; language: Language }
>(async ({ input }) => {
  const { reasonForVisit, language } = input;
  if (!reasonForVisit) return null;

  try {
    const result = await getQuestionnaireBy({
      visitReason: QUESTIONNAIRE_VISIT_REASON[reasonForVisit],
      language: language,
    });
    return result ?? null;
  } catch (error) {
    // A stale client from a previous deploy calls an action ID the server no longer knows.
    // Hide the questions instead of surfacing Next's internal error message to the patient.
    if (unstable_isUnrecognizedActionError(error)) {
      captureException(error);
      return null;
    }
    throw new Error(
      error instanceof Error
        ? error.message
        : "An unknown error occurred while fetching questions.",
    );
  }
});
